import React, { useMemo, useRef, useState } from 'react'

import { Container } from './Layers.styles'

import { Layer, LayerType } from 'modules/shares/types'
import { InspectorState } from 'modules/shares/Inspector/types'

import {
  isGroupLayerWithTints,
  isGroupLayerWithExports,
  isGroupLayerWithStyles,
} from './Layers.utils'
import { isMac } from '@sketch/utils'
import { LayerTree, unflattenLayers } from '../ContextMenu/layerTree'
import { LayersTestOverlay } from './LayersTestOverlay'

// Layer types that shouldn't appear in the inspector
const SKIP_LAYER_TYPES: LayerType[] = ['MSImmutableHotspotLayer']

/**
 * TYPES
 */
interface LayersProps {
  zoomFactor: number
  inspector: InspectorState
}

type LayerSearchContext = {
  isInsideGroupWithTints: boolean
  isInsideGroupWithExports: boolean
  isInsideGroupWithStyles: boolean
}

function findLayerAtPoint(
  layerTree: LayerTree,
  point: { x: number; y: number },
  skipGroups: boolean = false,
  // Accumulator object where we keep track of the groups we're currently inside
  // Depending on these properties, we decide to not select certain layers
  searchContext: LayerSearchContext = {
    isInsideGroupWithTints: false,
    isInsideGroupWithExports: false,
    isInsideGroupWithStyles: false,
  }
): Layer | undefined {
  if (!layerTree.layer) return undefined

  const layer = layerTree.layer

  // We want to avoid rendering some layers:
  // - A layer that is part of a combined shape (shapeGroup)
  // - Some types of layers, defined in SKIP_LAYER_TYPES
  // - Locked layers
  if (
    layer.isCombined ||
    layer.isLocked ||
    SKIP_LAYER_TYPES.includes(layer.type)
  ) {
    return undefined
  }

  // Copy the context so we don't mutate the original object
  // while we're traversing the tree
  const ctxForChildren = { ...searchContext }

  // Updating the context, so we know if we're inside a group with tints, exports or styles
  if (!ctxForChildren.isInsideGroupWithTints) {
    ctxForChildren.isInsideGroupWithTints = isGroupLayerWithTints(layer)
  }

  if (!ctxForChildren.isInsideGroupWithExports) {
    ctxForChildren.isInsideGroupWithExports = isGroupLayerWithExports(layer)
  }

  if (!ctxForChildren.isInsideGroupWithStyles) {
    ctxForChildren.isInsideGroupWithStyles = isGroupLayerWithStyles(layer)
  }

  const frame = layer.frame
  const x = frame.x
  const y = frame.y
  const maxX = x + frame.width
  const maxY = y + frame.height

  const isPointInsideLayer =
    point.x >= x && point.x <= maxX && point.y >= y && point.y <= maxY

  // If this layer isn't below the point, then we don't need to check its children, we can just return early
  if (!isPointInsideLayer) return undefined

  // Checking the children, before returning the layer itself
  for (let i = layerTree.children.length - 1; i >= 0; i--) {
    const child = layerTree.children[i]

    const found = findLayerAtPoint(child, point, skipGroups, ctxForChildren)

    if (found) return found
  }

  // We render symbols instances detached, so there are some situations
  // where we want to make a layer unclickable:
  // - when the layer is a group layer (a symbol instance is just a group
  //   layer) and ⌘ key is pressed
  // - when a layer has a parent group layer (a symbol instance) that has
  //   a Tint (because we want to select the Tint layer instead)
  // - when a layer has a parent group layer (symbol instance) that has
  //   exports (because we want to select the export layer instead)
  // - when a layer belongs to a symbol instance (a group layer) and this
  //   symbol instance is styled (because we want to select the symbol
  //   instance instead)
  // - when a layer has a mask sibling and a parent group that has
  //   noexports, no enabled style options or any tint applied (because
  //   we want to select the mask instead of the bitmap)
  //
  // Note: we are identifying symbol instances checking the `layer.type`
  // in hasParentGroupWith* functions, but it should be easier and less
  // error prone if we just check `isSymbolRoot`.
  const isGroupAndCommandKeyIsPressed = layer.type === 'group' && skipGroups

  const isLayerWithParentGroupWithTints =
    layer.type !== 'text' && searchContext.isInsideGroupWithTints

  const isLayerWithParentGroupWithExports =
    layer.type !== 'text' && searchContext.isInsideGroupWithExports

  const isLayerWithParentGroupWithStyles =
    layer.type !== 'text' && searchContext.isInsideGroupWithStyles

  const isMaskedLayer =
    !!layer.maskedBy &&
    !isLayerWithParentGroupWithTints &&
    !isLayerWithParentGroupWithExports &&
    !isLayerWithParentGroupWithStyles

  const isLayerUnclickable =
    isGroupAndCommandKeyIsPressed ||
    isLayerWithParentGroupWithTints ||
    isLayerWithParentGroupWithExports ||
    isLayerWithParentGroupWithStyles ||
    isMaskedLayer

  if (isLayerUnclickable) return undefined

  // At this point, we know that the layer is below the point and it's selectable
  return layer
}

const IS_RUNNING_TESTS =
  process.env.JEST_WORKER_ID !== undefined || window.Cypress

/**
 * MAIN COMPONENT
 * Renders layers in the inspector canvas where a user can hover and click
 */
const Layers: React.FC<LayersProps> = ({
  zoomFactor,
  inspector: { onHoverLayer, onClickLayer, data: inspectorData },
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [skippingGroups, setSkippingGroups] = useState<boolean>(false)

  // Building the layers tree, which makes our lives easier when we need to
  // find a layer at a specific point
  const layersTree = useMemo(() => {
    if (!inspectorData?.layers) return []

    const sortedLayers = Object.values(inspectorData.layers)

    // First, we sort the layers, so the tree is built in the correct order
    sortedLayers.sort((a, b) => (a.order as number) - (b.order as number))

    return unflattenLayers(sortedLayers)
  }, [inspectorData?.layers])

  function findLayerAtMousePosition(x: number, y: number) {
    if (!containerRef) return

    const containerBounds = containerRef.current!.getBoundingClientRect()

    // Converting the mouse position into artboard space, so we don't need to
    // recalculate the position of each layer after the user pans or zooms
    const point = {
      x: (x - containerBounds.left) / zoomFactor,
      y: (y - containerBounds.top) / zoomFactor,
    }

    // The layer at the end of the array, is the most visible one
    for (let i = layersTree.length - 1; i >= 0; i--) {
      const layer = layersTree[i]

      const found = findLayerAtPoint(layer, point, skippingGroups)

      if (found) return found
    }

    return undefined
  }

  // Holding ⌘ (mac) or ⌃ (win) while the user is moving around the inspector
  // will make the groups unclickable
  const handleMouseMove = (event: React.MouseEvent): void => {
    const isCommandKeyPressed = isMac() ? event.metaKey : event.ctrlKey

    if (isCommandKeyPressed !== skippingGroups) {
      setSkippingGroups(isCommandKeyPressed)
    }

    const layer = findLayerAtMousePosition(event.clientX, event.clientY)

    onHoverLayer(layer?.id)
  }

  const handleMouseLeave = (): void => {
    onHoverLayer(undefined)
  }

  const handleTouchMove = (event: React.TouchEvent): void => {
    const myLocation = event.changedTouches[0]

    const layer = findLayerAtMousePosition(
      myLocation.clientX,
      myLocation.clientY
    )

    onHoverLayer(layer?.id)
  }

  const handleTouchEnd = (): void => {
    onHoverLayer(undefined)
  }

  const handleClick = (event: React.MouseEvent): void => {
    event.stopPropagation()

    const layer = findLayerAtMousePosition(event.clientX, event.clientY)

    onClickLayer?.(layer?.id)
  }

  return (
    <Container
      ref={containerRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      {...rest}
    >
      {/*
       * Instead of relying on the browser pointer events to detect which layer was clicked,
       * for performance reasons, we perform the calculation manually, using the mouse position.
       * This makes testing a bit harder, because there aren't any divs to click on, so we would
       * have to hardcode the position of each layer. But, to make testing easier, we can render an overlay
       * of transparent divs for each layer, for testing purposes, so we can just use HTML selectors to find the position of a layer
       */}
      {IS_RUNNING_TESTS && (
        <LayersTestOverlay
          layers={inspectorData?.layers}
          zoomFactor={zoomFactor}
          layerTypesToSkip={SKIP_LAYER_TYPES}
        />
      )}
    </Container>
  )
}

export default Layers
